import { forwardRef } from 'react'
import { X } from 'lucide-react'
import { GestorDashboardBreakdownList, GestorDashboardMetricCard } from '@/components/Gestores/GestorDashboardWidgets'
import { GestorDashboardTimeTable } from '@/components/Gestores/GestorDashboardTimeTable'
import { formatEffortMinutes } from '@/components/Gestores/gestorDashboardFormat'
import {
  buildExecutorTaskRows,
  summarizeExecutorEntries,
} from '@/components/Gestores/gestorDashboardExecutorUtils'
import type { GestorDashboardTimeEntry } from '@/components/Gestores/gestorDashboardTypes'

type Props = {
  executor: string
  entries: GestorDashboardTimeEntry[]
  loading?: boolean
  onClear: () => void
  onTaskOpen: (taskId: number) => void
}

export const GestorDashboardExecutorPanel = forwardRef<HTMLElement, Props>(function GestorDashboardExecutorPanel(
  { executor, entries, loading = false, onClear, onTaskOpen },
  ref,
) {
  const summary = summarizeExecutorEntries(entries)
  const taskRows = buildExecutorTaskRows(entries).map((row) => ({
    key: row.key,
    label: row.workspace ? `${row.task} · ${row.workspace}` : row.task,
    taskId: row.taskId,
    effortMinutes: row.effortMinutes,
  }))

  return (
    <section ref={ref} className='space-y-3 rounded-xl border border-accent/25 bg-accent/[0.04] p-4 sm:p-5'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <p className='text-xs font-semibold uppercase tracking-wide text-accent'>Colaborador selecionado</p>
          <h3 className='mt-1 text-base font-semibold text-text-primary'>{executor}</h3>
          <p className='mt-0.5 text-xs text-text-muted'>
            Tarefas segmentadas por esforço no período filtrado.
          </p>
        </div>
        <button
          type='button'
          onClick={onClear}
          className='inline-flex items-center gap-1.5 rounded-lg border border-[var(--glass-border)] px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-white/[0.04] hover:text-text-primary'
        >
          <X size={14} />
          Limpar seleção
        </button>
      </div>

      <div className='grid gap-3 sm:grid-cols-2 xl:grid-cols-4'>
        <GestorDashboardMetricCard label='Esforço total' value={formatEffortMinutes(summary.totalEffortMinutes)} compact />
        <GestorDashboardMetricCard
          label='Horas produtivas'
          value={formatEffortMinutes(summary.productiveEffortMinutes)}
          tone='success'
          compact
        />
        <GestorDashboardMetricCard label='Tarefas distintas' value={String(summary.taskCount)} compact />
        <GestorDashboardMetricCard label='Apontamentos' value={String(summary.apontamentos)} compact />
      </div>

      <GestorDashboardBreakdownList
        title={`Tarefas de ${executor}`}
        rows={taskRows}
        onTaskOpen={onTaskOpen}
      />

      <GestorDashboardTimeTable entries={entries} loading={loading} onTaskOpen={onTaskOpen} />
    </section>
  )
})
